import { useState, useEffect } from "react";
import { useLocalStorage } from "@/hooks/useLocalStorage";

const LOGIN_PROMO_SHOWN_KEY = "shownAnyPromoThisLogin";

/**
 * Custom hook to handle the exercise promo modal logic
 * Shows the modal once after successful login, until January 31, 2026
 * @returns {[boolean, function]} - [showModal, handleClose]
 */
export function useExercisePromo() {
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [hasSeenPromo, setHasSeenPromo] = useLocalStorage("exercisePromoSeen", false);

  useEffect(() => {
    const shouldShowPromo = () => {
      // Ensure only one promo is displayed per login.
      if (localStorage.getItem(LOGIN_PROMO_SHOWN_KEY) === "true") {
        return false;
      }

      // Check if this is a fresh login (flag set by Login component)
      const showPromoFlag = localStorage.getItem("showExercisePromo");
      if (showPromoFlag !== "true") {
        return false;
      }

      // Check if user has already seen the promo
      if (hasSeenPromo) {
        // Clear the flag even if already seen
        localStorage.removeItem("showExercisePromo");
        return false;
      }

      // Check if we're still before the end date (January 31, 2026)
      const endDate = new Date("2026-01-31T23:59:59");
      const now = new Date();
      if (now > endDate) {
        // Clear the flag if past end date
        localStorage.removeItem("showExercisePromo");
        return false;
      }

      // Check if user is logged in (has access token)
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        localStorage.removeItem("showExercisePromo");
        return false;
      }

      return true;
    };

    // Small delay to ensure the page is fully loaded
    const timer = setTimeout(() => {
      if (shouldShowPromo()) {
        localStorage.setItem(LOGIN_PROMO_SHOWN_KEY, "true");
        setShowPromoModal(true);
        // Clear the flag so it doesn't show again on refresh
        localStorage.removeItem("showExercisePromo");
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [hasSeenPromo]);

  const handlePromoModalClose = () => {
    setShowPromoModal(false);
    setHasSeenPromo(true);
  };

  return [showPromoModal, handlePromoModalClose];
}



